import { useEffect, useState, type ReactNode } from 'react';
import { ApiError, cancelBooking, fetchBooking, fetchBookingSlots, locationLabel, rescheduleBooking, type Availability, type BookingStatus, type CustomerBooking } from '../api';
import { formatDateTime } from '../format';
import { applyBrandColor } from '../theme';
import { ProviderInfo, SchedulerCard, SlotPicker } from './book';

/** Buchungen und Anfragen lassen sich absagen und verschieben; abgesagt, abgelehnt und verfallen nicht mehr. */
function isActive(status: BookingStatus): boolean {
  return status === 'confirmed' || status === 'pending';
}

/** Die persönlichen Links aus der Bestätigungsmail: /absage/<link> und /umbuchen/<link>. */
export function ManagePage({ token, mode }: { token: string; mode: 'cancel' | 'reschedule' }) {
  const [booking, setBooking] = useState<CustomerBooking | null | undefined>(undefined);

  useEffect(() => {
    fetchBooking(token)
      .then((b) => {
        setBooking(b);
        applyBrandColor(b.brand.brandColor);
        document.title = b.brand.name;
      })
      .catch(() => setBooking(null));
  }, [token]);

  if (booking === undefined) return null;
  if (booking === null) {
    return (
      <main className="page page--message">
        <h1>Diesen Termin gibt es nicht</h1>
        <p className="muted">Bitte prüfe den Link aus deiner Bestätigungsmail.</p>
      </main>
    );
  }

  return (
    <>
      <main className={`page${mode === 'reschedule' && isActive(booking.status) ? ' page--wide' : ''}`}>
        {mode === 'cancel' ? <CancelView token={token} booking={booking} onChange={setBooking} /> : <RescheduleView token={token} booking={booking} onChange={setBooking} />}
        <p className="faint small">Alle Zeiten in deutscher Zeit.</p>
      </main>
      {(booking.brand.imprintUrl || booking.brand.privacyUrl) && (
        <footer className="footer">
          {booking.brand.imprintUrl && <a href={booking.brand.imprintUrl}>Impressum</a>}
          {booking.brand.privacyUrl && <a href={booking.brand.privacyUrl}>Datenschutz</a>}
        </footer>
      )}
    </>
  );
}

function Summary({ booking }: { booking: CustomerBooking }) {
  return (
    <p className="muted">
      {booking.eventType.title} · {booking.eventType.durationMinutes} Minuten · {locationLabel(booking.eventType.location)}
    </p>
  );
}

function CancelledNotice({ booking }: { booking: CustomerBooking }) {
  const title = booking.status === 'declined' ? 'Diese Anfrage passte leider nicht' : booking.status === 'expired' ? 'Diese Anfrage ist verfallen' : booking.cancelledBy === 'host' ? 'Dieser Termin wurde abgesagt' : 'Dein Termin ist abgesagt';
  return (
    <div className="card">
      <h1>{title}</h1>
      <p className="booked-when">{formatDateTime(booking.startsAt)}</p>
      <Summary booking={booking} />
      <p>
        Du möchtest einen neuen Termin? <a href={`/${booking.brand.slug}/${booking.eventType.slug}`}>Hier geht es zur Buchung.</a>
      </p>
    </div>
  );
}

function CancelView({ token, booking, onChange }: { token: string; booking: CustomerBooking; onChange: (b: CustomerBooking) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!isActive(booking.status) && !done) return <CancelledNotice booking={booking} />;
  const pending = booking.status === 'pending';
  if (done) {
    return (
      <div className="card">
        <h1>{pending ? 'Deine Anfrage ist zurückgezogen' : 'Dein Termin ist abgesagt'}</h1>
        <p className="booked-when">{formatDateTime(booking.startsAt)}</p>
        <p>Schade – aber kein Problem. Wenn es später passt, buch dir gern einen neuen Termin.</p>
        <p>
          <a className="button button--secondary" href={`/${booking.brand.slug}/${booking.eventType.slug}`}>
            Neuen Termin buchen
          </a>
        </p>
      </div>
    );
  }

  async function cancel() {
    setBusy(true);
    setError(null);
    try {
      onChange(await cancelBooking(token));
      setDone(true);
    } catch {
      setError('Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card--brand">
      <p className="label label--brand">{booking.brand.name}</p>
      <h1>{pending ? 'Anfrage zurückziehen?' : 'Termin absagen?'}</h1>
      <p className="booked-when">{formatDateTime(booking.startsAt)}</p>
      <Summary booking={booking} />
      {pending && <p className="muted small">Diese Anfrage wartet noch auf die Bestätigung durch {booking.brand.name}.</p>}
      <p>Wenn du nur eine andere Zeit brauchst: Verschieben geht auch, dann bleibt alles andere, wie es ist.</p>
      {error && <div className="error">{error}</div>}
      <div className="save-bar">
        <button type="button" className="button button--danger" disabled={busy} onClick={() => void cancel()}>
          {busy ? 'Einen Moment …' : pending ? 'Ja, Anfrage zurückziehen' : 'Ja, Termin absagen'}
        </button>
        <a className="link-button" href={`/umbuchen/${token}`}>
          Lieber verschieben
        </a>
      </div>
    </div>
  );
}

function RescheduleView({ token, booking, onChange }: { token: string; booking: CustomerBooking; onChange: (b: CustomerBooking) => void }) {
  const [availability, setAvailability] = useState<Availability | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const load = () =>
    fetchBookingSlots(token)
      .then(setAvailability)
      .catch(() => setAvailability(null));

  useEffect(() => {
    if (isActive(booking.status)) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, booking.status]);

  if (!isActive(booking.status)) return <CancelledNotice booking={booking} />;

  async function choose(slot: string) {
    setBusy(true);
    setError(null);
    try {
      onChange(await rescheduleBooking(token, slot));
      setDone(true);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'slot_taken') {
        setError('Diese Uhrzeit ist gerade vergeben worden. Bitte such dir eine andere aus.');
        void load();
      } else if (e instanceof ApiError && e.code === 'google_unavailable') {
        setError('Gerade lässt sich der Kalender nicht prüfen. Bitte versuch es in ein paar Minuten noch einmal – dein bisheriger Termin bleibt bestehen.');
        void load();
      } else setError('Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      setBusy(false);
    }
  }

  const picking = !done && !!availability && !availability.unavailable && availability.days.length > 0;
  const info = (
    <ProviderInfo brand={booking.brand} eventType={booking.eventType} heading="h2" chosen={done ? booking.startsAt : null}>
      {!done && (
        <p className="muted small scheduler__note">
          Bisher: <strong>{formatDateTime(booking.startsAt)}</strong>
        </p>
      )}
    </ProviderInfo>
  );

  let main: ReactNode;
  if (done) {
    main = (
      <div className="confirm">
        <h2 className="confirm__title">{booking.status === 'pending' ? 'Deine Anfrage hat eine neue Zeit' : 'Dein neuer Termin steht'}</h2>
        <p className="booked-when">{formatDateTime(booking.startsAt)}</p>
        <Summary booking={booking} />
        <p>{booking.status === 'pending' ? `${booking.brand.name} schaut sich die Anfrage an und meldet sich per E-Mail.` : 'Du bekommst gleich eine neue Bestätigung per E-Mail.'}</p>
      </div>
    );
  } else if (availability === undefined) main = <p className="muted scheduler__status">Freie Termine werden geladen …</p>;
  else if (availability === null) main = <div className="error">Die Termine lassen sich gerade nicht laden. Bitte versuch es gleich noch einmal.</div>;
  else if (availability.unavailable) main = <div className="notice">Gerade können wir keine Zeiten anzeigen. Bitte versuch es in ein paar Minuten noch einmal – dein bisheriger Termin bleibt bestehen.</div>;
  else if (availability.days.length === 0) main = <div className="notice">Zurzeit ist leider nichts anderes frei. Dein bisheriger Termin bleibt bestehen.</div>;
  else main = <SlotPicker days={availability.days} disabled={busy} onPick={(slot) => void choose(slot)} />;

  return (
    <>
      {!done && (
        <div>
          <h1>Termin verschieben</h1>
          <p className="muted">Such dir eine neue Zeit aus – alles andere bleibt, wie es ist.</p>
        </div>
      )}
      {error && <div className="error">{error}</div>}
      <SchedulerCard wide={picking} info={info}>
        {main}
      </SchedulerCard>
      {!done && (
        <p className="small">
          <a href={`/absage/${token}`}>Termin stattdessen absagen</a>
        </p>
      )}
    </>
  );
}
